import React from 'react';
import { RefreshCw, BarChart2 } from 'lucide-react';
import { InnerLayout } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { Button } from '../components/ui/Button';
import { useAnalyticsData } from './analytics/hooks/useAnalyticsData';
import { AnalyticsKpiGrid } from './analytics/components/AnalyticsKpiGrid';
import { AnalyticsVolumeChart } from './analytics/components/AnalyticsVolumeChart';

export const AnalyticsView: React.FC = () => {
  const {
    project,
    tables,
    timeRange,
    setTimeRange,
    isLoading,
    analyticsData,
    latencyP50,
    latencyP95,
    latencyP99,
    totalSubmissions,
    validSubmissions,
    spamSubmissions,
    timeSeriesBuckets,
    dynamicCount,
    strictCount,
    displaySpamRate,
    displayCleanRate,
    fetchAnalyticsAndLogs,
  } = useAnalyticsData();

  if (isLoading && !project) {
    return (
      <div className="flex items-center justify-center h-96">
        <HedgehogSpinner size="lg" label="AGGREGATING TELEMETRY..." />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-8 border border-dashed border-cf-border rounded-lg bg-cf-surface/30">
        <BarChart2 size={36} className="text-zinc-600 mb-3" />
        <h3 className="text-sm font-semibold text-cf-textPrimary">No Project Selected</h3>
        <p className="text-xs text-cf-textSecondary mt-1 max-w-sm">
          Please select or create a project from the sidebar to view ingestion telemetry and latency percentiles.
        </p>
      </div>
    );
  }

  return (
    <InnerLayout
      title="Ingestion Analytics"
      description="Real-time pipeline latency percentiles, honeypot spam quarantine, and delivery metrics."
      actions={
        <div className="flex items-center gap-2">
          {/* Time Range Selector */}
          <div className="flex items-center p-0.5 rounded bg-cf-surface border border-cf-border gap-0.5">
            {(['24h', '7d', '30d'] as const).map((r) => (
              <Button
                key={r}
                size="xs"
                variant={timeRange === r ? 'secondary' : 'ghost'}
                onClick={() => setTimeRange(r)}
                className={`text-xs px-2.5 py-1 ${
                  timeRange === r
                    ? 'bg-cf-surfaceActive text-cf-orange font-semibold'
                    : 'text-cf-textSecondary hover:text-cf-textPrimary'
                }`}
              >
                {r.toUpperCase()}
              </Button>
            ))}
          </div>

          {/* Refresh Button */}
          <Button
            size="icon-xs"
            variant="outline"
            onClick={() => fetchAnalyticsAndLogs(true)}
            disabled={isLoading}
            title="Refresh Analytics"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} />
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Bento Grid */}
        <AnalyticsKpiGrid
          totalSubmissions={totalSubmissions}
          validSubmissions={validSubmissions}
          spamSubmissions={spamSubmissions}
          latencyP50={latencyP50}
          latencyP95={latencyP95}
          latencyP99={latencyP99}
          tablesCount={tables.length}
          dynamicCount={dynamicCount}
          strictCount={strictCount}
          storageBytes={analyticsData?.storage_bytes}
          filesCount={analyticsData?.files_count}
          displaySpamRate={displaySpamRate}
          displayCleanRate={displayCleanRate}
        />

        {/* Time-Series Submission Volume Chart */}
        <AnalyticsVolumeChart
          timeSeriesBuckets={timeSeriesBuckets}
          timeRange={timeRange}
          totalSubmissions={totalSubmissions}
          spamSubmissions={spamSubmissions}
        />
      </div>
    </InnerLayout>
  );
};
